import { h } from 'vue';
import type { DataTableColumnDef } from '@alphadoc/alphadoc-table-grid';
import { DateTime } from 'luxon';
import {
  sectionTableColumn,
  sectionTableDateColumn,
  sectionTableNumberColumn,
} from '@/modules/core/utils/section-table-column.util.ts';
import type { CounterpartyBankDetails } from '@/modules/counterparty/types/counterparty-bank-details.type.ts';
import type { CounterpartyAddress } from '@/modules/counterparty/types/counterparty-address.type.ts';
import type { CounterpartyContact } from '@/modules/counterparty/types/counterparty-contact.type.ts';
import type { CounterpartyComment } from '@/modules/counterparty/types/counterparty-comment.type.ts';
import type { Counterparty } from '@/modules/counterparty/types/counterparty.type.ts';
import type { DocumentLinkRow } from '@/modules/common/types/document-link-row.type.ts';
import { CounterpartyDistinctOptionsUtil } from '@/modules/counterparty/utils/counterparty-distinct-options.util.ts';
import { CounterpartyOptionsUtil } from '@/modules/counterparty/utils/counterparty-options.util.ts';
import { DocumentCellsUtil } from '@/modules/document/utils/document-cells.util.ts';
import UiProgressCircle from '@/modules/ui/components/ui/UiProgressCircle.vue';
import { ElementType } from '@/modules/viewer/enums/element-type.enum.ts';

export const bankDetailsSectionColumns: DataTableColumnDef<CounterpartyBankDetails>[] = [
  sectionTableColumn('bank', (row) => row['Банк'], 180, { distinct: true }),
  sectionTableColumn('bik', (row) => row['БИК'], 120),
  sectionTableColumn('ks', (row) => row['КС'], 180),
  sectionTableColumn('rs', (row) => row['РС'], 180),
  sectionTableColumn('note', (row) => row['Примечание'] ?? '', 200),
];

export const addressesSectionColumns: DataTableColumnDef<CounterpartyAddress>[] = [
  sectionTableColumn('type', (row) => row['Тип'], 120, { distinct: true }),
  sectionTableColumn('address', (row) => row['Адрес'], 280),
  sectionTableColumn('note', (row) => row['Примечание'] ?? '', 200),
];

export const contactsSectionColumns: DataTableColumnDef<CounterpartyContact>[] = [
  sectionTableColumn('fullName', (row) => row['ФИО'] ?? '', 180, { distinct: true }),
  sectionTableColumn('position', (row) => row['Должность'] ?? '', 140, { distinct: true }),
  sectionTableDateColumn('birthDay', (row) => row['ДР'], 120, DateTime.DATE_SHORT),
  sectionTableColumn('type', (row) => row['Тип'], 100, { distinct: true }),
  sectionTableColumn('contact', (row) => row['Контакт'], 160),
  sectionTableColumn('note', (row) => row['Примечание'] ?? '', 180),
];

export const commentsSectionColumns: DataTableColumnDef<CounterpartyComment>[] = [
  sectionTableDateColumn('dateTime', (row) => row['Дата время'], 160),
  sectionTableColumn('comment', (row) => row['Комментарий'], 320),
  sectionTableColumn('user', (row) => row['Пользователь'], 160, { distinct: true }),
];

export const nearestDocsSectionColumns: DataTableColumnDef<DocumentLinkRow>[] = [
  sectionTableColumn('link', (row) => row['Ссылка'] ?? '', 140, { distinct: true }),
  sectionTableColumn('type', (row) => row['Тип'], 140, { distinct: true }),
  sectionTableColumn('active', (row) => !!row['Активен'], 96, {
    type: ElementType.Boolean,
    cell: (value) => DocumentCellsUtil.active(value as boolean),
  }),
  sectionTableNumberColumn('number', (row) => row['Номер'], 100),
  sectionTableDateColumn('date', (row) => row['Дата'], 140, DateTime.DATE_SHORT),
  sectionTableColumn('controller', (row) => row['Контролер'] ?? '', 160, { distinct: true }),
  sectionTableColumn('project', (row) => row['Проект'] ?? '', 160, { distinct: true }),
  sectionTableColumn('content', (row) => row['Содержание'] ?? '', 280),
  sectionTableDateColumn('deadline', (row) => row['Срок'], 140, DateTime.DATE_SHORT),
  sectionTableColumn('execution', (row) => row['Выполнение'] ?? null, 120, {
    type: ElementType.Number,
    cell: (value) => {
      const progress = value as number | null;
      return progress !== null
        ? h(UiProgressCircle, { value: progress, completedTooltip: 'control.progressFull' })
        : '';
    },
  }),
  sectionTableColumn('user', (row) => row['Пользователь'] ?? '', 160, { distinct: true }),
  sectionTableDateColumn('created', (row) => row['Создан'], 140, DateTime.DATE_SHORT),
  sectionTableColumn('regNumber', (row) => row['Рег.номер'] ?? '', 120),
  sectionTableDateColumn('registration', (row) => row['Регистрация'], 140, DateTime.DATE_SHORT),
];

export const counterpartiesSelectColumns: DataTableColumnDef<Counterparty>[] = [
  sectionTableNumberColumn('number', (row) => row.number, 100),
  sectionTableColumn('name', (row) => row.name, 240),
  sectionTableColumn('type', (row) => row.type, 120, {
    type: ElementType.Enum,
    enumOptions: CounterpartyOptionsUtil.types,
  }),
  sectionTableColumn('legalForm', (row) => row.legalForm ?? '', 100, {
    type: ElementType.Enum,
    enumOptions: CounterpartyOptionsUtil.legalForms,
  }),
  sectionTableColumn('region', (row) => row.region, 140, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.region,
  }),
  sectionTableColumn('segment', (row) => row.segment, 140, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.segment,
  }),
  sectionTableColumn('city', (row) => row.city, 140, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.city,
  }),
  sectionTableColumn('manager', (row) => row.manager, 160, {
    type: ElementType.Enum,
    enumOptions: CounterpartyDistinctOptionsUtil.manager,
  }),
  sectionTableColumn('note', (row) => row.note ?? '', 180),
];
